import { v4 as uuidv4 } from 'uuid'

const VISITOR_STORAGE_KEY = 'cybersec:visitor_id'
const VISITOR_COOKIE_NAME = 'cybersec_vid'
const VISITOR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 // 1 year

const isBrowser = typeof window !== 'undefined'
const isValidVisitorId = (value?: string | null) =>
  typeof value === 'string' && /^[\da-f]{8}-[\da-f]{4}-[1-5][\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i.test(value.trim())

const readCookie = (name: string): string | undefined => {
  if (!isBrowser || !document.cookie) return undefined
  const entries = document.cookie.split(';')
  for (const entry of entries) {
    const idx = entry.indexOf('=')
    if (idx === -1) continue
    const key = entry.slice(0, idx).trim()
    if (key !== name) continue
    const value = entry.slice(idx + 1)
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  return undefined
}

const writeCookie = (name: string, value: string) => {
  if (!isBrowser) return
  const secure = window.location.protocol === 'https:'
  const sameSite = secure ? 'None' : 'Lax'
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${VISITOR_COOKIE_MAX_AGE_SECONDS}`,
    `SameSite=${sameSite}`,
  ]
  if (secure) {
    parts.push('Secure')
  }
  document.cookie = parts.join('; ')
}

const persistVisitorId = (value: string) => {
  if (!value) return
  try {
    localStorage.setItem(VISITOR_STORAGE_KEY, value)
  } catch {
    // localStorage may be unavailable; ignore
  }
  try {
    writeCookie(VISITOR_COOKIE_NAME, value)
  } catch {
    // ignore cookie write failures
  }
}

/**
 * Returns the stable visitor identifier, generating one if necessary.
 * Stored in both localStorage and a SameSite cookie so the backend can correlate visits.
 */
export const getOrCreateVisitorId = (): string | undefined => {
  if (!isBrowser) return undefined

  let stored: string | undefined
  try {
    stored = localStorage.getItem(VISITOR_STORAGE_KEY) || undefined
  } catch {
    stored = undefined
  }

  if (!isValidVisitorId(stored)) {
    stored = readCookie(VISITOR_COOKIE_NAME)
  }

  if (!isValidVisitorId(stored)) {
    stored = (crypto?.randomUUID?.() ?? uuidv4())
  }

  if (stored) {
    persistVisitorId(stored)
  }

  return stored
}

export const getVisitorCookieName = () => VISITOR_COOKIE_NAME
